import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface LeadershipMember {
  name: string;
  title: string;
  role: string;
  image: string;
  description: string;
}

const churchTimeline = [
  { year: '2016', titleKey: 'aboutPage.timeline.2016.title', descKey: 'aboutPage.timeline.2016.desc', icon: 'ri-building-line' },
  { year: '2017', titleKey: 'aboutPage.timeline.2017.title', descKey: 'aboutPage.timeline.2017.desc', icon: 'ri-hammer-line' },
  { year: '2018', titleKey: 'aboutPage.timeline.2018.title', descKey: 'aboutPage.timeline.2018.desc', icon: 'ri-award-line' },
  { year: '2019', titleKey: 'aboutPage.timeline.2019.title', descKey: 'aboutPage.timeline.2019.desc', icon: 'ri-user-star-line' },
  { year: '2024', titleKey: 'aboutPage.timeline.2024.title', descKey: 'aboutPage.timeline.2024.desc', icon: 'ri-image-2-line' },
  { year: '2025', titleKey: 'aboutPage.timeline.2025.title', descKey: 'aboutPage.timeline.2025.desc', icon: 'ri-building-line' },
  { year: '2026', titleKey: 'aboutPage.timeline.2026.title', descKey: 'aboutPage.timeline.2026.desc', icon: 'ri-cake-3-line' },
];

const copticHistoryMilestones = [
  { era: 'Origins', eraKey: 'aboutPage.copticHistory.origins.era', titleKey: 'aboutPage.copticHistory.origins.title', descKey: 'aboutPage.copticHistory.origins.desc', image: '/assets/readdy/3ea97320fefe508706914fd1a965d05e-5481b0c7d5ad.png' },
  { era: '42 AD', eraKey: null, titleKey: 'aboutPage.copticHistory.stMark.title', descKey: 'aboutPage.copticHistory.stMark.desc', image: '/assets/readdy/b6c52c8348fadbbd93c5ed74bb9b469e-08fa7ac244b0.png' },
  { era: 'Councils', eraKey: 'aboutPage.copticHistory.councils.era', titleKey: 'aboutPage.copticHistory.councils.title', descKey: 'aboutPage.copticHistory.councils.desc', image: '/assets/readdy/bc60106de5c4039046dadb40ff752f66-e8838136fd72.png' },
  { era: 'Beliefs', eraKey: 'aboutPage.copticHistory.beliefs.era', titleKey: 'aboutPage.copticHistory.beliefs.title', descKey: 'aboutPage.copticHistory.beliefs.desc', image: '/assets/readdy/0d3d2fde42c1d0f1df6a8a0a5e513368-913cd8e3ed1f.webp' },
  { era: 'Traditions', eraKey: 'aboutPage.copticHistory.traditions.era', titleKey: 'aboutPage.copticHistory.traditions.title', descKey: 'aboutPage.copticHistory.traditions.desc', image: '/assets/readdy/36a62d59a519ea7fe8ff2262b7650208-4453ee1f0a28.png' },
  { era: 'Today', eraKey: 'aboutPage.copticHistory.today.era', titleKey: 'aboutPage.copticHistory.today.title', descKey: 'aboutPage.copticHistory.today.desc', image: '/assets/readdy/21d42728371f396f8f85a55154c80e9c-263bc9c34d42.png' },
];

const saints = [
  {
    nameKey: 'aboutPage.saints.stMina.name',
    titleKey: 'aboutPage.saints.stMina.title',
    descKey: 'aboutPage.saints.stMina.desc',
    image: '/assets/readdy/a819e154-7847-4c75-899f-b7f02f0a9915_IMG_0518-0c77e3583d1e.jpeg',
    feastKey: 'aboutPage.saints.stMina.feast'
  },
  {
    nameKey: 'aboutPage.saints.popeCyril.name',
    titleKey: 'aboutPage.saints.popeCyril.title',
    descKey: 'aboutPage.saints.popeCyril.desc',
    image: '/assets/readdy/acf5d81a-7621-4035-b51f-5b78fc09c87e_IMG_0509-af3d7b64dee9.jpeg',
    feastKey: 'aboutPage.saints.popeCyril.feast'
  }
];

const leadership = [
  { nameKey: 'aboutPage.leadership.popeTawadros.name', titleKey: 'aboutPage.leadership.popeTawadros.title', descKey: 'aboutPage.leadership.popeTawadros.desc', role: 'pope', image: '/assets/readdy/ed2190d4522749c3675b16fa11c4cf46-e2711b899710.png' },
  { nameKey: 'aboutPage.leadership.bishopBoulos.name', titleKey: 'aboutPage.leadership.bishopBoulos.title', descKey: 'aboutPage.leadership.bishopBoulos.desc', role: 'bishop', image: '/assets/readdy/b073ceb25798d0e74cb37f9a44eef647-f69c779c3ae5.png' },
  { nameKey: 'aboutPage.leadership.frAnthony.name', titleKey: 'aboutPage.leadership.frAnthony.title', descKey: 'aboutPage.leadership.frAnthony.desc', role: 'priest', image: '/assets/readdy/f46f13081ecf4f52070ca15241409afa-e78d0b782d54.png' },
];

const boardMembers = [
  { name: 'H.G. Bishop Boulos', position: 'President', image: '/assets/readdy/b073ceb25798d0e74cb37f9a44eef647-f69c779c3ae5.png' },
  { name: 'Fr. Anthony Micheal', position: 'Vice-President', image: '/assets/readdy/f46f13081ecf4f52070ca15241409afa-e78d0b782d54.png' },
  { name: 'Osama Habshy', position: 'Treasurer', image: '/assets/readdy/41c4c49ed35ea235cdaf9de51bcb081d-61b7b0163acd.png' },
  { name: 'Fady Habashy', position: 'Secretary', image: '/assets/readdy/cb0361233ed6b2ed11ac4ab149f36e69-868f2443a309.png' },
  { name: 'Kamal Atallah', position: 'Member', image: '/assets/readdy/58b0d31de736f10541c0a270db99db51-b84fc15454a9.png' }
];

export default function AboutPage() {
  const { t } = useTranslation();
  const [activeHistoryIndex, setActiveHistoryIndex] = useState(0);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const timelineSectionRef = useRef<HTMLElement>(null);

  return (
    <PageLayout hasHero>
      <PageHero 
        title={t('aboutPage.title')}
        subtitle={t('aboutPage.subtitle')}
        backgroundImage="/assets/readdy/12a0372e-72d0-467f-b20b-84c1aaa54b27_WhatsApp-Image-2026-03-22-at-13.46.08-6dc97fd7a4bf.jpeg"
      />

      {/* Our Mission: Serving the North Shore Section */}
      <PageSectionNav
        sections={[
          { id: 'mission', label: t('aboutPage.ourMission') },
          { id: 'timeline', label: t('aboutPage.churchTimeline') },
          { id: 'coptic-church', label: t('aboutPage.copticChurchTitle') },
          { id: 'patron-saints', label: t('aboutPage.patronSaints') },
          { id: 'leadership', label: t('aboutPage.spiritualFathers') },
          { id: 'mission-vision', label: t('aboutPage.ourMissionTitle') + ' & ' + t('aboutPage.ourVision') },
        ]}
      />

      {/* Our Mission: Serving the North Shore Section */}
      <section id="mission" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#d4a017]/20 text-[#8b6914] text-sm font-medium rounded-full mb-4">
                {t('aboutPage.ourMission')}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
                {t('aboutPage.servingNorthShore')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('aboutPage.servingNorthShoreDesc1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {t('aboutPage.servingNorthShoreDesc2')}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-5 shadow-md">
                  <div className="w-12 h-12 bg-[#3d1a1e] rounded-full flex items-center justify-center mb-3">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{t('aboutPage.strategicLocation')}</h4>
                  <p className="text-gray-600 text-sm">{t('aboutPage.strategicLocationDesc')}</p>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-md">
                  <div className="w-12 h-12 bg-[#d4a017] rounded-full flex items-center justify-center mb-3">
                    <i className="ri-community-line text-xl text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{t('aboutPage.growingCommunity')}</h4>
                  <p className="text-gray-600 text-sm">{t('aboutPage.growingCommunityDesc')}</p>
                </div>
              </div>
            </div>

            {/* Right: Map */}
            <div className="relative flex flex-col gap-4">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d178838.95234567!2d-73.85!3d45.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc922a5a5a5a5a5%3A0x1234567890abcdef!2sNorth%20Shore%2C%20Montreal%2C%20QC!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="North Shore Montreal Map"
                  className="w-full h-[280px] md:h-[450px]"
                ></iframe>
              </div>
              {/* Service Area Card — inline on mobile, overlay on desktop */}
              <div className="lg:absolute lg:top-4 lg:right-4 bg-[#3d1a1e]/85 backdrop-blur-sm text-white rounded-2xl p-4 shadow-lg w-full lg:max-w-[240px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#d4a017] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-xl"></i>
                  </div>
                  <span className="font-bold text-sm">{t('aboutPage.ourServiceArea')}</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t('aboutPage.serviceAreaList')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Church Timeline Section */}
      <section id="timeline" ref={timelineSectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#3d1a1e]/10 text-[#3d1a1e] text-sm font-medium rounded-full mb-4">
              {t('aboutPage.ourJourney')}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('aboutPage.churchTimeline')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              {t('aboutPage.timelineDesc')}
            </p>
            <button
              onClick={() => setTimelineExpanded(!timelineExpanded)}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#3d1a1e] text-white font-semibold rounded-full hover:bg-[#d4a017] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className={`${timelineExpanded ? 'ri-eye-off-line' : 'ri-time-line'} text-xl`}></i>
              {timelineExpanded ? t('aboutPage.hideTimeline') : t('aboutPage.viewTimeline')}
              <i className={`${timelineExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl transition-transform`}></i>
            </button>
          </div>

          {/* Timeline - Collapsible */}
          <div
            className={`overflow-hidden transition-all duration-700 ease-in-out ${
              timelineExpanded ? 'max-h-[3000px] opacity-100 mt-8' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="relative max-w-4xl mx-auto">
              {/* Timeline Line */}
              <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#3d1a1e] via-[#d4a017] to-[#3d1a1e] rounded-full"></div>

              <div className="space-y-12">
                {churchTimeline.map((event, index) => (
                  <div 
                    key={event.year}
                    className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Content Card */}
                    <div className={`w-full md:w-5/12 ml-20 md:ml-0 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                      <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100 group cursor-pointer">
                        <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                          <div className="w-12 h-12 bg-[#3d1a1e] rounded-full flex items-center justify-center group-hover:bg-[#d4a017] transition-colors flex-shrink-0">
                            <i className={`${event.icon} text-white text-xl`}></i>
                          </div>
                          <span className="text-2xl font-bold text-[#3d1a1e]">{event.year}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{t(event.titleKey)}</h3>
                        <p className="text-gray-600 leading-relaxed">{t(event.descKey)}</p>
                      </div>
                    </div>

                    {/* Center Dot */}
                    <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 bg-[#d4a017] rounded-full border-4 border-white shadow-lg z-10"></div>

                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block w-5/12"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Collapse button at bottom - only when expanded */}
          {timelineExpanded && (
            <div className="text-center mt-12 relative z-20">
              <button
                onClick={() => {
                  setTimelineExpanded(false);
                  setTimeout(() => {
                    timelineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3d1a1e] text-white font-semibold rounded-full hover:bg-[#d4a017] transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                <i className="ri-arrow-up-s-line text-lg"></i>
                {t('aboutPage.collapseTimeline')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Coptic Orthodox Church History Section */}
      <section id="coptic-church" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#d4a017]/20 text-[#8b6914] text-sm font-medium rounded-full mb-4">
              {t('aboutPage.yearsOfFaith')}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('aboutPage.copticChurchTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.copticChurchDesc')}
            </p>
          </div>

          {/* Interactive History Cards */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Left: Era Selector */}
            <div className="space-y-4 flex flex-col">
              {copticHistoryMilestones.map((milestone, index) => (
                <button
                  key={milestone.era}
                  onClick={() => setActiveHistoryIndex(index)}
                  className={`w-full text-left p-5 rounded-2xl transition-all cursor-pointer ${
                    activeHistoryIndex === index 
                      ? 'bg-[#3d1a1e] text-white shadow-lg' 
                      : 'bg-white hover:bg-gray-50 text-gray-900 shadow'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activeHistoryIndex === index ? 'bg-[#d4a017]' : 'bg-[#3d1a1e]/10'
                    }`}>
                      <span className={`text-sm font-bold ${
                        activeHistoryIndex === index ? 'text-white' : 'text-[#3d1a1e]'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#d4a017]">
                        {milestone.eraKey ? t(milestone.eraKey) : milestone.era}
                      </p>
                      <h4 className="text-lg font-bold truncate">{t(milestone.titleKey)}</h4>
                    </div>
                    <i className={`ri-arrow-right-line ml-auto text-xl flex-shrink-0 ${
                      activeHistoryIndex === index ? 'text-[#d4a017]' : 'text-gray-400'
                    }`}></i>
                  </div>
                </button>
              ))}
            </div>

            {/* Right: Image Display */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[320px] sm:h-[400px] lg:h-full">
              <img 
                src={copticHistoryMilestones[activeHistoryIndex].image}
                alt={t(copticHistoryMilestones[activeHistoryIndex].titleKey)}
                className="w-full h-full object-cover transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
                <span className="inline-block px-3 py-1 bg-[#d4a017] text-white text-xs sm:text-sm font-bold rounded-full mb-2 sm:mb-3">
                  {copticHistoryMilestones[activeHistoryIndex].era}
                </span>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-white mb-2 sm:mb-3 drop-shadow-lg">
                  {t(copticHistoryMilestones[activeHistoryIndex].titleKey)}
                </h3>
                <p className="text-white/90 text-sm sm:text-base lg:text-lg leading-relaxed drop-shadow-md">
                  {t(copticHistoryMilestones[activeHistoryIndex].descKey)}
                </p>
              </div>
            </div>
          </div>

          {/* Key Facts */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-14 h-14 bg-[#3d1a1e] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <p className="text-3xl font-bold text-[#3d1a1e] mb-1">42 AD</p>
              <p className="text-gray-600 text-sm">{t('aboutPage.churchFounded')}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-14 h-14 bg-[#3d1a1e] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-2xl text-white"></i>
              </div>
              <p className="text-3xl font-bold text-[#3d1a1e] mb-1">20M+</p>
              <p className="text-gray-600 text-sm">{t('aboutPage.faithfulWorldwide')}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-14 h-14 bg-[#3d1a1e] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-4-line text-2xl text-white"></i>
              </div>
              <p className="text-3xl font-bold text-[#3d1a1e] mb-1">118</p>
              <p className="text-gray-600 text-sm">{t('aboutPage.popesOfAlexandria')}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-14 h-14 bg-[#3d1a1e] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-3-line text-2xl text-white"></i>
              </div>
              <p className="text-3xl font-bold text-[#3d1a1e] mb-1">210+</p>
              <p className="text-gray-600 text-sm">{t('aboutPage.fastingDays')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Saints Section */}
      <section id="patron-saints" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#3d1a1e]/10 text-[#3d1a1e] text-sm font-medium rounded-full mb-4">
              {t('aboutPage.heavenlyIntercessors')}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              {t('aboutPage.patronSaints')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.patronSaintsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {saints.map((saint) => (
              <div key={saint.nameKey} className="group">
                <div className="relative h-[350px] rounded-2xl overflow-hidden shadow-lg mb-4">
                  <img 
                    src={saint.image}
                    alt={t(saint.nameKey)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="inline-block px-3 py-1 bg-[#d4a017] text-white text-xs font-medium rounded-full mb-2">
                      {t(saint.feastKey)}
                    </span>
                    <h3 className="text-xl font-bold text-white">{t(saint.nameKey)}</h3>
                    <p className="text-white/80 text-sm">{t(saint.titleKey)}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{t(saint.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section id="leadership" className="py-20 bg-gradient-to-b from-[#3d1a1e] to-[#2a1215]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-white/20 text-white text-sm font-medium rounded-full mb-4">
              {t('aboutPage.spiritualGuidance')}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              {t('aboutPage.spiritualFathers')}
            </h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              {t('aboutPage.spiritualFathersDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {leadership.map((leader) => (
              <div key={leader.nameKey} className="bg-white rounded-3xl overflow-hidden shadow-2xl group">
                <div className="relative h-[300px] overflow-hidden">
                  <img 
                    src={leader.image}
                    alt={t(leader.nameKey)}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-1.5 text-white text-xs font-bold rounded-full uppercase tracking-wider ${
                      leader.role === 'pope' ? 'bg-[#d4a017]' : 
                      leader.role === 'bishop' ? 'bg-[#8b6914]' : 'bg-[#3d1a1e]'
                    }`}>
                      {leader.role}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{t(leader.nameKey)}</h3>
                  <p className="text-[#d4a017] font-medium mb-3">{t(leader.titleKey)}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{t(leader.descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Board Members */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-serif font-bold text-white mb-3">{t('aboutPage.churchBoard')}</h3>
              <p className="text-white/70">{t('aboutPage.churchBoardDesc')}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {boardMembers.map((member) => (
                <div key={member.name} className="text-center group">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-3 border-white/30 group-hover:border-[#d4a017] transition-colors">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{member.name}</h4>
                  <p className="text-[#d4a017] text-xs">{member.position}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section id="mission-vision" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#3d1a1e] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">{t('aboutPage.ourMissionTitle')}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('aboutPage.ourMissionDesc')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.missionCelebrate')}</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.missionTeach')}</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.missionCommunity')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#d4a017] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">{t('aboutPage.ourVision')}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('aboutPage.ourVisionDesc')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.visionExpand')}</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.visionYouth')}</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#d4a017] text-xl"></i>
                  <span>{t('aboutPage.visionOutreach')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-[#3d1a1e]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            {t('aboutPage.joinFamily')}
          </h2>
          <p className="text-white/80 text-lg mb-8">
            {t('aboutPage.joinFamilyDesc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/services" 
              className="px-8 py-4 bg-[#d4a017] text-white font-semibold rounded-full hover:bg-[#b8930f] transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              {t('aboutPage.viewServiceTimes')}
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
